/**
 * PRASHASTA-SETUP-02 — Gate 3
 * BusinessSetup.jsx — Centralized Business Setup hub
 *
 * Nine frozen setup groups, searchable config navigation,
 * readiness indicators from existing backend APIs.
 * All config items deep-link to existing canonical pages.
 *
 * D4 capability guards:
 *   OrganizationSetup    → admin.users        (backend: require_roles SETUP_MANAGER_ROLES)
 *   FinanceSetup         → ledger.read        (backend: require_capability ledger.read)
 *   EventModelSetup      → admin.users        (backend: require_roles SETUP_MANAGER_ROLES)
 *   EventOpsMDMSetup     → event.ops.read     (backend: require_capability event.ops.read)
 *   AgentGovernanceSetup → admin.users        (backend: require_roles SETUP_MANAGER_ROLES)
 *   HomepageAdmin        → catalog.manage     (backend: require_capability catalog.manage on writes)
 *   EmailChannelSetup    → comms.channel.read (backend: require_capability comms.channel.read)
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, Calendar, DollarSign, Package,
  UserCog, MessageSquare, Zap, ShieldCheck,
  Search, ChevronRight, CheckCircle2, AlertCircle, Circle,
  Settings2, ArrowRight,
} from "lucide-react";
import { setupApi } from "@/services/api";

// ── Static group + item manifest ──────────────────────────────────────────────
const GROUPS = [
  {
    id: "company",
    seq: "01",
    label: "Company Setup",
    description: "Legal entities, office locations, enterprise structure and tenant profile",
    icon: Building2,
    domainCodes: ["01-TENANT-PROFILE", "02-ORGANIZATION"],
    items: [
      {
        label: "Organization Structure",
        description: "Legal entities, operating units, branch locations",
        to: "/organization",
        testId: "biz-setup-org-structure",
      },
      {
        label: "Enterprise Structure",
        description: "Business hierarchy, financial structure, service taxonomy",
        to: "/enterprise",
        testId: "biz-setup-enterprise",
      },
      {
        label: "Setup Domains",
        description: "Configuration lifecycle status for all platform domains",
        to: "/setup-studio",
        testId: "biz-setup-domains",
      },
    ],
  },
  {
    id: "crm",
    seq: "02",
    label: "CRM Setup",
    description: "Enquiry flows, service catalog, pricing studio and digital presence",
    icon: Users,
    domainCodes: ["08-PRICING-STUDIO"],
    items: [
      {
        label: "Service Catalog",
        description: "Services your business offers to clients",
        to: "/crm/catalog",
        testId: "biz-setup-service-catalog",
      },
      {
        label: "Concierge Widget",
        description: "Guest enquiry flow, conversation questions and visibility settings",
        to: "/administration?tab=concierge",
        testId: "biz-setup-concierge",
      },
      {
        label: "Homepage & Booking Journey",
        description: "Categories and featured content shown to guests",
        to: "/homepage-admin",
        testId: "biz-setup-homepage",
      },
      {
        label: "Pricing Studio",
        description: "Rate cards, service pricing, taxes and estimate configuration",
        to: "/pricing-studio",
        testId: "biz-setup-pricing",
      },
      {
        label: "CRM Configuration",
        description: "Urgency thresholds and Co-Pilot guidance customisation",
        to: "/crm-configuration",
        testId: "biz-setup-crm-config",
      },
    ],
  },
  {
    id: "event",
    seq: "03",
    label: "Event Setup",
    description: "Event taxonomy, operations master data and event finance types",
    icon: Calendar,
    domainCodes: ["05-EVENT-MODEL"],
    items: [
      {
        label: "Event Model Foundation",
        description: "Event types, service categories and service definitions",
        to: "/event-model",
        testId: "biz-setup-event-model",
      },
      {
        label: "Event Operations MDM",
        description: "Venue types, catering types, logistics and operations reference data",
        to: "/event-ops-mdm",
        testId: "biz-setup-event-ops-mdm",
      },
      {
        label: "Event Finance Config",
        description: "Advance requirements and financial types per event category",
        to: "/finance/setup",
        testId: "biz-setup-event-finance",
      },
    ],
  },
  {
    id: "finance",
    seq: "04",
    label: "Finance Setup",
    description: "Fiscal calendar, chart of accounts, currencies and AR master data",
    icon: DollarSign,
    domainCodes: ["04-BASE-FINANCE"],
    items: [
      {
        label: "Finance Setup",
        description: "Fiscal periods, currencies, cost centres and payment terms",
        to: "/finance/setup",
        testId: "biz-setup-finance",
      },
      {
        label: "AR Configuration",
        description: "Receipt methods, credit note reasons and numbering series",
        to: "/ar/master-data",
        testId: "biz-setup-ar-config",
      },
      {
        label: "Approval Policies",
        description: "Budget approval thresholds and authorization rules",
        to: "/settings/approval-policies",
        testId: "biz-setup-approval-policies",
      },
    ],
  },
  {
    id: "procurement",
    seq: "05",
    label: "Procurement & Sourcing Setup",
    description: "Supplier registry and sourcing package configuration",
    icon: Package,
    domainCodes: [],
    items: [
      {
        label: "Supplier Registry",
        description: "Approved vendors, service providers and partner profiles",
        to: "/enterprise/suppliers",
        testId: "biz-setup-suppliers",
      },
    ],
  },
  {
    id: "people",
    seq: "06",
    label: "People & Access",
    description: "User accounts, role packs, capabilities and identity security",
    icon: UserCog,
    domainCodes: ["03-IDENTITY-SECURITY"],
    items: [
      {
        label: "Users & Roles",
        description: "Staff accounts, UAT personas and role assignments",
        to: "/administration/users",
        testId: "biz-setup-users",
      },
      {
        label: "Role Packs & Permissions",
        description: "Capability packs and role-level access configuration",
        to: "/administration",
        testId: "biz-setup-permissions",
      },
    ],
  },
  {
    id: "communications",
    seq: "07",
    label: "Communications Setup",
    description: "Channel registry, WhatsApp, email, Facebook and Instagram",
    icon: MessageSquare,
    domainCodes: ["06-COMMUNICATION"],
    items: [
      {
        label: "Channel Registry",
        description: "Overview and health of all configured communication channels",
        to: "/administration/communications",
        testId: "biz-setup-comms-registry",
      },
      {
        label: "Email Channel",
        description: "SMTP and provider configuration for outbound email",
        to: "/email-channel",
        testId: "biz-setup-email-channel",
      },
      {
        label: "WhatsApp & Social Channels",
        description: "WhatsApp Business API, Facebook and Instagram configuration",
        to: "/administration/communications",
        testId: "biz-setup-social-channels",
      },
    ],
  },
  {
    id: "integrations",
    seq: "08",
    label: "Integrations & Data",
    description: "Third-party integrations, API connections and data import management",
    icon: Zap,
    domainCodes: [],
    items: [
      {
        label: "Integration Control Center",
        description: "Configured external service integrations and connection status",
        to: "/administration/integrations",
        testId: "biz-setup-integrations",
      },
      {
        label: "Data Imports",
        description: "Bulk import of leads, contacts and master data records",
        to: "/administration/imports",
        testId: "biz-setup-imports",
      },
    ],
  },
  {
    id: "governance",
    seq: "09",
    label: "Security & Governance",
    description: "Audit trail, agent governance, skills and escalation policies",
    icon: ShieldCheck,
    domainCodes: ["07-AGENT-GOVERNANCE"],
    items: [
      {
        label: "Audit Trail",
        description: "Full system event log with tenant isolation and filtering",
        to: "/administration/audit",
        testId: "biz-setup-audit",
      },
      {
        label: "Agent Governance Foundation",
        description: "Agent skill definitions, policies and escalation rules",
        to: "/agent-governance",
        testId: "biz-setup-agent-governance",
      },
    ],
  },
];

// ── Readiness helpers ─────────────────────────────────────────────────────────
function groupReadiness(group, domainStatuses) {
  if (!group.domainCodes.length) return "neutral";
  const entries = group.domainCodes
    .map((code) => domainStatuses[code])
    .filter(Boolean);
  if (!entries.length) return "neutral";

  // Resolve each entry to { status, percentage }
  const resolved = entries.map((e) =>
    typeof e === "object" ? e : { status: e, percentage: 0 }
  );

  const statuses = resolved.map((e) => e.status);
  const anyProgress = resolved.some((e) => (e.percentage ?? 0) > 0);

  if (statuses.every((s) => s === "ACTIVE")) return "green";
  // Amber: some domain is explicitly configured/active, OR domain is NOT_STARTED but has progress
  if (
    statuses.some((s) => ["ACTIVE", "CONFIGURED", "VALIDATED"].includes(s)) ||
    anyProgress
  )
    return "amber";
  return "red";
}

function ReadinessDot({ status }) {
  if (status === "green")
    return (
      <span data-testid="readiness-green" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle2 size={10} className="shrink-0" />
        Configured
      </span>
    );
  if (status === "amber")
    return (
      <span data-testid="readiness-amber" className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
        <AlertCircle size={10} className="shrink-0" />
        Partial
      </span>
    );
  if (status === "red")
    return (
      <span data-testid="readiness-red" className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
        <Circle size={10} className="shrink-0" />
        Not started
      </span>
    );
  return (
    <span data-testid="readiness-neutral" className="inline-flex items-center gap-1 text-xs font-medium text-[#706E6B] bg-[#F3F2F2] border border-[#DDDBDA] rounded-full px-2 py-0.5">
      <Circle size={10} className="shrink-0" />
      No domain
    </span>
  );
}

// ── Group card ────────────────────────────────────────────────────────────────
function GroupCard({ group, domainStatuses, search, onNavigate }) {
  const navigate = useNavigate();
  const readiness = groupReadiness(group, domainStatuses);
  const IconComp = group.icon;

  const filteredItems = useMemo(() => {
    if (!search) return group.items;
    const q = search.toLowerCase();
    return group.items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
    );
  }, [group.items, search]);

  if (search && filteredItems.length === 0) return null;

  return (
    <div
      data-testid={`biz-setup-group-${group.id}`}
      className="bg-white border border-[#DDDBDA] rounded-xl flex flex-col overflow-hidden hover:shadow-md transition-shadow duration-200"
    >
      {/* Card header */}
      <div className="px-5 pt-5 pb-4 border-b border-[#F3F2F2]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#EEF4FF] flex items-center justify-center shrink-0">
              <IconComp size={18} className="text-[#0176D3]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-[#706E6B] tracking-widest uppercase leading-none">
                  {group.seq}
                </span>
              </div>
              <h3
                data-testid={`biz-setup-group-${group.id}-title`}
                className="text-sm font-semibold text-[#181818] leading-tight mt-0.5"
              >
                {group.label}
              </h3>
            </div>
          </div>
          <ReadinessDot status={readiness} />
        </div>
        <p className="text-xs text-[#706E6B] mt-2 leading-relaxed">{group.description}</p>
      </div>

      {/* Items list */}
      <div className="flex-1 divide-y divide-[#F3F2F2]">
        {filteredItems.map((item) => (
          <button
            key={item.to + item.label}
            data-testid={item.testId}
            onClick={() => navigate(item.to)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-[#F8F7F6] transition-colors group"
          >
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#181818] truncate group-hover:text-[#0176D3] transition-colors">
                {item.label}
              </p>
              <p className="text-[11px] text-[#706E6B] mt-0.5 leading-relaxed line-clamp-1">
                {item.description}
              </p>
            </div>
            <ChevronRight size={14} className="shrink-0 text-[#DDDBDA] group-hover:text-[#0176D3] transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BusinessSetup() {
  const [search, setSearch] = useState("");
  const [domainStatuses, setDomainStatuses] = useState({});
  const [readinessLoading, setReadinessLoading] = useState(true);

  useEffect(() => {
    setupApi
      .getReadiness()
      .then((r) => {
        // Normalise response: could be { domain_readiness: { code: { status } } } or flat map
        // Each entry is stored as { status, percentage } to enable amber for NOT_STARTED+progress>0
        const raw = r.data;
        const map = {};
        if (raw.domain_readiness) {
          Object.entries(raw.domain_readiness).forEach(([code, val]) => {
            map[code] = typeof val === "object"
              ? { status: val.status, percentage: val.percentage ?? 0 }
              : { status: val, percentage: 0 };
          });
        }
        if (raw.domains) {
          raw.domains.forEach((d) => {
            map[d.code] = { status: d.status, percentage: d.percentage ?? 0 };
          });
        }
        setDomainStatuses(map);
      })
      .catch(() => {
        /* readiness is non-critical; show neutral indicators on failure */
      })
      .finally(() => setReadinessLoading(false));
  }, []);

  const configuredCount = useMemo(() => {
    return GROUPS.filter((g) => {
      const r = groupReadiness(g, domainStatuses);
      return r === "green";
    }).length;
  }, [domainStatuses]);

  const visibleGroups = useMemo(() => {
    if (!search.trim()) return GROUPS;
    const q = search.toLowerCase();
    return GROUPS.filter(
      (g) =>
        g.label.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.items.some(
          (i) =>
            i.label.toLowerCase().includes(q) ||
            i.description.toLowerCase().includes(q)
        )
    );
  }, [search]);

  return (
    <div
      data-testid="business-setup-page"
      style={{ fontFamily: "'Inter', 'Outfit', sans-serif" }}
      className="min-h-screen bg-[#F6F3EF] px-4 sm:px-6 lg:px-10 py-8"
    >
      {/* Page header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Settings2 size={16} className="text-[#706E6B]" />
              <span className="text-xs text-[#706E6B] uppercase tracking-widest font-semibold">Settings</span>
              <ChevronRight size={12} className="text-[#DDDBDA]" />
              <span className="text-xs text-[#0176D3] font-semibold">Business Setup</span>
            </div>
            <h1
              data-testid="business-setup-title"
              className="text-2xl font-bold text-[#181818]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Business Setup
            </h1>
            <p className="text-sm text-[#706E6B] mt-1">
              Configure your business across nine areas. All changes are tenant-isolated and audit-logged.
            </p>
          </div>

          {/* Readiness summary */}
          {!readinessLoading && (
            <div
              data-testid="business-setup-readiness-summary"
              className="flex items-center gap-2 bg-white border border-[#DDDBDA] rounded-lg px-4 py-2.5 text-sm"
            >
              <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
              <span className="text-[#706E6B]">
                <span className="font-semibold text-[#181818]">{configuredCount}</span> of{" "}
                <span className="font-semibold text-[#181818]">9</span> groups configured
              </span>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#706E6B] pointer-events-none"
          />
          <input
            data-testid="business-setup-search"
            type="text"
            placeholder="Search setup groups and configuration items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-[#DDDBDA] rounded-lg text-[#181818] placeholder-[#ADB5BD] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] hover:text-[#181818]"
            >
              ×
            </button>
          )}
        </div>

        {/* Search result count */}
        {search && (
          <p className="text-xs text-[#706E6B] mb-4">
            {visibleGroups.length === 0
              ? "No configuration items matched your search."
              : `${visibleGroups.length} group${visibleGroups.length !== 1 ? "s" : ""} found`}
          </p>
        )}

        {/* Group cards grid */}
        <div
          data-testid="business-setup-groups-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {visibleGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              domainStatuses={domainStatuses}
              search={search}
            />
          ))}
        </div>

        {/* Empty search state */}
        {search && visibleGroups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search size={32} className="text-[#DDDBDA] mb-3" />
            <p className="text-sm font-medium text-[#706E6B]">No results for "{search}"</p>
            <button
              onClick={() => setSearch("")}
              className="mt-3 text-xs text-[#0176D3] hover:underline"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Gate 3 compliance note */}
        <div className="mt-8 flex items-start gap-2.5 bg-white border border-[#DDDBDA] rounded-lg px-4 py-3">
          <ShieldCheck size={14} className="text-[#706E6B] mt-0.5 shrink-0" />
          <p className="text-xs text-[#706E6B] leading-relaxed">
            <span className="font-medium text-[#181818]">PRASHASTA-SETUP-02</span> — Business Setup consolidation.
            All configuration items open existing canonical pages. No operational data, purchase orders, invoices or payments
            are managed here. Each item is protected by its canonical backend capability guard.
            Production deployment remains unauthorized until Gate 4 UAT is complete.
          </p>
        </div>
      </div>
    </div>
  );
}
